import { useState } from 'react'
import CapstonMark from '../components/CapstonMark'
import { api } from '../lib/api'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// Passwordless login (#27): the form requests a magic link; the reply is the
// same neutral confirmation whether or not the address has access, so this
// public page never reveals who the admins are.
export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState(null)
  const [serverError, setServerError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [sentTo, setSentTo] = useState(null)

  async function handleSubmit(event) {
    event.preventDefault()
    if (submitting) return

    const trimmed = email.trim()
    let error = null
    if (!trimmed) error = 'Email is required.'
    else if (!EMAIL_RE.test(trimmed)) error = 'Enter a valid email address.'
    setFieldError(error)
    setServerError(null)
    if (error) return

    setSubmitting(true)
    try {
      await api('/api/v1/auth/request-link', {
        method: 'POST',
        body: { email: trimmed },
      })
      setSentTo(trimmed)
    } catch (err) {
      setServerError(err.detail || 'Something went wrong — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (sentTo) {
    return (
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--color-surface-alt)' }}
      >
        <div className="card card--panel w-full" style={{ maxWidth: '420px' }}>
          <CapstonMark size={32} />
          <p className="overline" style={{ marginTop: 'var(--space-m)' }}>Capston</p>
          <h1 className="text-h3" style={{ marginBottom: 'var(--space-m)' }}>
            Check your email
          </h1>
          <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }} role="status">
              If <strong>{sentTo}</strong> has dashboard access, a sign-in link
              is on its way. The link works once and expires in 1 hour.
            </p>
            <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Nothing arriving? Check spam, or request a new link — the newest
              one replaces any earlier ones.
            </p>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setSentTo(null)}
            >
              Request another link
            </button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--color-surface-alt)' }}
    >
      <div className="card card--panel w-full" style={{ maxWidth: '420px' }}>
        <CapstonMark size={32} />
        <p className="overline" style={{ marginTop: 'var(--space-m)' }}>Capston</p>
        <h1 className="text-h3" style={{ marginBottom: 'var(--space-m)' }}>
          Experience
        </h1>

        {serverError && (
          <div className="alert alert--error text-body-sm" style={{ marginBottom: 'var(--space-s)' }} role="alert">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
          <div className="field">
            <label className="field__label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              className={`field__input${fieldError ? ' field__input--invalid' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {fieldError && <p className="field__error">{fieldError}</p>}
          </div>

          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Email me a sign-in link'}
          </button>
        </form>
      </div>
    </main>
  )
}
